import type { User } from '@auth0/auth0-react'
import { type PayloadAction, createSlice } from '@reduxjs/toolkit'
import type { Role, UserDto } from '../../types/coreApi-types'

export interface SessionState {
  isLoading: boolean
  id: string
  auth0Id: string
  role: Role
  token: string
  otacToken: string
  impersonationToken: string
  isImpersonating: boolean
  impersonationExpiresAtUtc: string
  adminBackupId: string
  adminBackupAuth0Id: string
  adminBackupRole?: Role
  adminBackupToken: string
  adminReturnPath: string
  user: UserDto
  auth0user?: User
}

const initialState: SessionState = {
  isLoading: true,
  id: '',
  auth0Id: '',
  token: '',
  otacToken: '',
  impersonationToken: '',
  isImpersonating: false,
  impersonationExpiresAtUtc: '',
  adminBackupId: '',
  adminBackupAuth0Id: '',
  adminBackupRole: undefined,
  adminBackupToken: '',
  adminReturnPath: '',
  // @ts-ignore (Session isLoading = false until User is set)
  user: undefined,
  auth0user: undefined,
}

export const sessionSlice = createSlice({
  name: 'session',
  initialState,
  reducers: {
    startSession: (
      state: SessionState,
      action: PayloadAction<{
        id: string
        auth0Id: string
        role: Role
        token: string
        auth0user: User
      }>
    ) => {
      if (state.isImpersonating) {
        // An active impersonation session was rehydrated after a browser
        // refresh. The Auth0 identity belongs to the admin, so refresh the
        // admin backup and the bearer token, but keep acting as the
        // impersonated target (id/role/impersonationToken stay untouched).
        state.adminBackupId = action.payload.id
        state.adminBackupAuth0Id = action.payload.auth0Id
        state.adminBackupRole = action.payload.role
        state.adminBackupToken = action.payload.token
        state.token = action.payload.token
        state.auth0Id = action.payload.auth0Id
        state.auth0user = action.payload.auth0user

        if (state.user) state.isLoading = false
        return
      }

      state.token = action.payload.token
      state.id = action.payload.id
      state.auth0Id = action.payload.auth0Id
      state.role = action.payload.role
      state.auth0user = action.payload.auth0user

      if (state.user) state.isLoading = false
    },
    startImpersonationSession: (
      state: SessionState,
      action: PayloadAction<{
        impersonationToken: string
        targetUserId: string
        targetRole: Role
        expiresAtUtc: string
        returnPath?: string
      }>
    ) => {
      if (!state.isImpersonating) {
        state.adminBackupId = state.id
        state.adminBackupAuth0Id = state.auth0Id
        state.adminBackupRole = state.role
        state.adminBackupToken = state.token
        state.adminReturnPath = action.payload.returnPath || ''
      }

      state.impersonationToken = action.payload.impersonationToken
      state.impersonationExpiresAtUtc = action.payload.expiresAtUtc
      state.isImpersonating = true
      state.id = action.payload.targetUserId
      state.role = action.payload.targetRole
      state.user = undefined as unknown as UserDto
      state.isLoading = true
    },
    stopImpersonationSession: (state: SessionState) => {
      if (!state.isImpersonating) return

      state.id = state.adminBackupId
      state.auth0Id = state.adminBackupAuth0Id
      if (state.adminBackupRole) {
        state.role = state.adminBackupRole
      }
      state.token = state.adminBackupToken

      state.impersonationToken = ''
      state.impersonationExpiresAtUtc = ''
      state.isImpersonating = false
      state.adminBackupId = ''
      state.adminBackupAuth0Id = ''
      state.adminBackupRole = undefined
      state.adminBackupToken = ''
      state.adminReturnPath = ''
      state.user = undefined as unknown as UserDto
      state.isLoading = true
    },
    endSession: (state: SessionState) => {
      state.token = initialState.token
      state.id = initialState.id
      state.auth0Id = initialState.auth0Id
      state.role = undefined as unknown as Role
      state.otacToken = initialState.otacToken
      state.impersonationToken = initialState.impersonationToken
      state.isImpersonating = initialState.isImpersonating
      state.impersonationExpiresAtUtc = initialState.impersonationExpiresAtUtc
      state.adminBackupId = initialState.adminBackupId
      state.adminBackupAuth0Id = initialState.adminBackupAuth0Id
      state.adminBackupRole = initialState.adminBackupRole
      state.adminBackupToken = initialState.adminBackupToken
      state.user = undefined as unknown as UserDto
      state.auth0user = initialState.auth0user
      state.isLoading = true
    },
    setUser: (
      state: SessionState,
      action: PayloadAction<{
        user: UserDto
      }>
    ) => {
      state.user = action.payload.user
      state.isLoading = false
    },
    setSessionLoading: (
      state: SessionState,
      action: PayloadAction<{ isLoading: boolean }>
    ) => {
      state.isLoading = action.payload.isLoading
    },
    setOtacToken: (
      state: SessionState,
      action: PayloadAction<{
        otacToken: string
      }>
    ) => {
      state.otacToken = action.payload.otacToken
    },
  },
})

export { initialState as sessionInitialState }

export const {
  startSession,
  startImpersonationSession,
  stopImpersonationSession,
  endSession,
  setSessionLoading,
  setUser,
  setOtacToken,
} = sessionSlice.actions

export default sessionSlice.reducer
